// report_builder/ConditionRow.jsx
// ============================================================
// CONDITION ROW
// Renders one condition in the report builder.
// Handles all condition types:
//   comparison, cross, offset_diff, prev_n_break,
//   candle_color, n_wrapper (any/all)
//
// Props:
//   condition    — condition object from report definition
//   index        — position in conditions array
//   registry     — { variables, operators } from API
//   onChange     — fn(index, updatedCondition)
//   onDelete     — fn(index)
//   onConnector  — fn(index, 'AND'|'OR') — changes connector
//   isFirst      — bool, hides connector selector on first row
// ============================================================

import React from 'react';

const TIMEFRAME_OPTIONS = ['auto', '2min', '5min', '15min', '1H', '1D', '1W'];
const OFFSET_OPTIONS    = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// ── Default side object ──────────────────────────────────────
const defaultSide = (variable = 'CLOSE') => ({
  variable,
  params: {
    timeframe: 'auto',
    offset:    0,
  },
});

// ── Default inner condition (used inside n_wrapper) ──────────
const defaultInner = () => ({
  type:     'comparison',
  left:     defaultSide('CLOSE'),
  operator: 'lt',
  right:    defaultSide('EMA'),
});

// ── Default condition ────────────────────────────────────────
export const defaultCondition = (id = 1) => ({
  id,
  type:      'comparison',
  connector: null,
  left:      defaultSide('CLOSE'),
  operator:  'gt',
  right:     { variable: 'EMA', params: { period: 20, on: 'close', timeframe: 'auto', offset: 0 } },
});


// ============================================================
// VARIABLE PARAM EDITORS
// Renders the param inputs for a chosen variable.
// ============================================================

function VariableParams({ variable, params, onChange, registry, isRhs }) {
  if (!variable || !registry) return null;
  const entry = registry.variables[variable];
  if (!entry) return null;

  const update = (key, val) => onChange({ ...params, [key]: val });

  return (
    <>
      {entry.params.map(param => {
        // Skip offset for PREV_N (no per-candle offset — N controls lookback)
        if (variable === 'PREV_N' && param.name === 'offset') return null;

        if (param.type === 'select') {
          return (
            <select
              key={param.name}
              value={params[param.name] ?? param.default ?? ''}
              onChange={e => {
                const val = param.options && typeof param.options[0] === 'number'
                  ? Number(e.target.value)
                  : e.target.value;
                update(param.name, val);
              }}
              style={s.select}
              title={param.label}
            >
              {param.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          );
        }

        if (param.type === 'number') {
          return (
            <input
              key={param.name}
              type="number"
              value={params[param.name] ?? param.default ?? 0}
              min={param.min ?? undefined}
              max={param.max ?? undefined}
              onChange={e => update(param.name, Number(e.target.value))}
              style={{ ...s.numInput, width: param.name === 'offset' ? '44px' : '56px' }}
              title={param.label}
              placeholder={param.label}
            />
          );
        }

        return null;
      })}
    </>
  );
}


// ============================================================
// SIDE PICKER
// LHS or RHS variable selector + its params.
// ============================================================

function SidePicker({ side, onChange, registry, isRhs, rhsType }) {
  if (!registry) return null;

  const { variable, params = {} } = side;

  // Build variable options based on isRhs and rhsType
  const varOptions = Object.entries(registry.variables)
    .filter(([, entry]) => {
      if (!isRhs && entry.rhs_only) return false;
      if (isRhs && entry.lhs_only) return false;
      // When operator forces prev_n — only show PREV_N
      if (rhsType === 'prev_n') return variable === 'PREV_N' ? true
        : ['PREV_N'].includes(entry.label === 'Prev N' ? 'PREV_N' : '');
      // When operator forces color — only show COLOR_VALUE
      if (rhsType === 'color') return ['COLOR_VALUE'].includes(key => key);
      return true;
    })
    .map(([key, entry]) => ({ key, label: entry.label, category: entry.category }));

  // Force variable if rhsType constrains it
  const effectiveVar = rhsType === 'prev_n'   ? 'PREV_N'
                     : rhsType === 'color'     ? 'COLOR_VALUE'
                     : variable;

  const handleVarChange = (newVar) => {
    // Reset params to defaults for new variable
    const entry = registry.variables[newVar];
    if (!entry) return;
    const newParams = {};
    entry.params.forEach(p => { newParams[p.name] = p.default ?? 0; });
    onChange({ variable: newVar, params: newParams });
  };

  const handleParamsChange = (newParams) => {
    onChange({ variable: effectiveVar, params: newParams });
  };

  // Group by category for display
  const categories = {};
  varOptions.forEach(({ key, label, category }) => {
    if (!categories[category]) categories[category] = [];
    categories[category].push({ key, label });
  });

  return (
    <div style={s.sidePicker}>
      {/* Variable selector — hidden if forced by rhsType */}
      {!rhsType || rhsType === 'variable' || rhsType === 'literal' ? (
        <select
          value={effectiveVar || ''}
          onChange={e => handleVarChange(e.target.value)}
          style={{ ...s.select, minWidth: '72px' }}
        >
          {Object.entries(categories).map(([cat, items]) => (
            <optgroup key={cat} label={cat}>
              {items.map(({ key, label }) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </optgroup>
          ))}
        </select>
      ) : (
        <span style={s.forcedVar}>
          {registry.variables[effectiveVar]?.label || effectiveVar}
        </span>
      )}

      {/* Params for the chosen variable */}
      <VariableParams
        variable={effectiveVar}
        params={params}
        onChange={handleParamsChange}
        registry={registry}
        isRhs={isRhs}
      />
    </div>
  );
}


// ============================================================
// OPERATOR SELECTOR
// ============================================================

function OperatorPicker({ operator, onChange, registry }) {
  if (!registry) return null;
  return (
    <select
      value={operator || ''}
      onChange={e => onChange(e.target.value)}
      style={{ ...s.select, minWidth: '132px', color: '#2962ff', fontWeight: 'bold' }}
    >
      {Object.entries(registry.operators).map(([key, entry]) => (
        <option key={key} value={key}>{entry.label}</option>
      ))}
    </select>
  );
}


// ============================================================
// EXTRA INPUT (for up_by / down_by)
// ============================================================

function ExtraInput({ operatorKey, extra = {}, onChange, registry }) {
  if (!registry) return null;
  const opEntry = registry.operators[operatorKey];
  if (!opEntry?.extra_input) return null;

  const update = (key, val) => onChange({ ...extra, [key]: val });

  return (
    <>
      {opEntry.extra_input.map(field => {
        if (field.type === 'number') {
          return (
            <input
              key={field.name}
              type="number"
              value={extra[field.name] ?? field.default ?? 0}
              min={field.min ?? 0}
              onChange={e => update(field.name, Number(e.target.value))}
              style={{ ...s.numInput, width: '52px' }}
              title={field.label}
              placeholder={field.label}
            />
          );
        }
        if (field.type === 'select') {
          return (
            <select
              key={field.name}
              value={extra[field.name] ?? field.default ?? ''}
              onChange={e => update(field.name, e.target.value)}
              style={s.select}
              title={field.label}
            >
              {field.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          );
        }
        return null;
      })}
    </>
  );
}


// ============================================================
// N WRAPPER INNER CONDITION
// Rendered when type = n_wrapper
// Shows: Any/All toggle + N input + inner condition row
// ============================================================

function NWrapperEditor({ condition, onChange, registry }) {
  const mode  = condition.mode  || 'any';
  const n     = condition.n     || 5;
  const inner = condition.inner || defaultInner();

  const update = (key, val) => onChange({ ...condition, [key]: val });

  // Inner condition change handler
  const handleInnerChange = (key, val) => {
    update('inner', { ...inner, [key]: val });
  };

  const opEntry    = registry?.operators[inner.operator];
  const rhsType    = opEntry?.rhs_type || 'variable';

  return (
    <div style={s.nWrapperBox}>
      {/* Mode + N controls */}
      <div style={s.nWrapperHeader}>
        <div style={s.toggleGroup}>
          <button
            style={{ ...s.toggleBtn, ...(mode === 'any' ? s.toggleBtnActive : {}) }}
            onClick={() => update('mode', 'any')}
          >Any</button>
          <button
            style={{ ...s.toggleBtn, ...(mode === 'all' ? s.toggleBtnActive : {}) }}
            onClick={() => update('mode', 'all')}
          >All</button>
        </div>
        <span style={s.nLabel}>of past</span>
        <input
          type="number"
          value={n}
          min={1}
          max={50}
          onChange={e => update('n', Number(e.target.value))}
          style={{ ...s.numInput, width: '48px' }}
        />
        <span style={s.nLabel}>candles:</span>
      </div>

      {/* Inner condition */}
      <div style={s.innerRow}>
        <SidePicker
          side={inner.left || defaultSide('CLOSE')}
          onChange={val => handleInnerChange('left', val)}
          registry={registry}
          isRhs={false}
        />
        <OperatorPicker
          key={`inner-op-${inner.operator}`}
          operator={inner.operator}
          onChange={val => {
            const newOp      = registry.operators[val];
            const newRhsType = newOp?.rhs_type || 'variable';
            const newCondType = newOp?.condition_type || 'comparison';
            let newRight     = inner.right;

            if (newRhsType === 'prev_n') {
              newRight = { variable: 'PREV_N', params: { field: 'high', n: 20, timeframe: 'auto' } };
            } else if (newRhsType === 'color') {
              newRight = { variable: 'COLOR_VALUE', params: { value: 'green' } };
            } else if (newCondType === 'cross') {
              const validForCross = ['CLOSE','OPEN','HIGH','LOW','EMA'];
              if (!validForCross.includes(inner.right?.variable)) {
                newRight = { variable: 'EMA', params: { period: 20, on: 'close', timeframe: 'auto', offset: 0 } };
              }
            } else if (['PREV_N','COLOR_VALUE'].includes(inner.right?.variable)) {
              newRight = { variable: 'EMA', params: { period: 20, on: 'close', timeframe: 'auto', offset: 0 } };
            }

            // Single atomic update — avoids sequential state overwrite
            onChange({
              ...condition,
              inner: {
                ...inner,
                type:     newCondType,
                operator: val,
                right:    newRight,
              }
            });
          }}
          registry={registry}
        />
        <SidePicker
          side={inner.right || defaultSide('EMA')}
          onChange={val => handleInnerChange('right', val)}
          registry={registry}
          isRhs={true}
          rhsType={rhsType}
        />
      </div>
    </div>
  );
}


// ============================================================
// CONDITION ROW — main export
// ============================================================

export default function ConditionRow({
  condition,
  index,
  registry,
  onChange,
  onDelete,
  onConnector,
  isFirst,
}) {
  if (!condition || !registry) return null;

  const { type, connector, operator, left, right, extra } = condition;

  // ── Operator change — may reset RHS ──────────────────────
  const handleOperatorChange = (newOp) => {
    const opEntry    = registry.operators[newOp];
    const newRhsType = opEntry?.rhs_type || 'variable';
    const newCondType = opEntry?.condition_type || 'comparison';

    let newRight = right;

    // Force correct RHS based on operator type
    if (newRhsType === 'none') {
      // No-RHS operators (V Pattern "Occurred")
      newRight = null;
    } else if (newRhsType === 'prev_n') {
      newRight = { variable: 'PREV_N', params: { field: 'high', n: 20, timeframe: 'auto' } };
    } else if (newRhsType === 'color') {
      newRight = { variable: 'COLOR_VALUE', params: { value: 'green' } };
    } else if (newCondType === 'cross') {
      // Cross operators need a valid field variable on RHS
      // Reset to EMA if current RHS is not a valid field variable
      const validForCross = ['CLOSE','OPEN','HIGH','LOW','EMA'];
      if (!right || !validForCross.includes(right?.variable)) {
        newRight = { variable: 'EMA', params: { period: 20, on: 'close', timeframe: 'auto', offset: 0 } };
      }
    } else if (newRhsType === 'variable') {
      // Switching back to regular comparison — reset if RHS was forced or absent
      if (!right || ['PREV_N', 'COLOR_VALUE'].includes(right?.variable)) {
        newRight = { variable: 'EMA', params: { period: 20, on: 'close', timeframe: 'auto', offset: 0 } };
      }
    }

    onChange(index, {
      ...condition,
      type:     newCondType,
      operator: newOp,
      right:    newRight,
    });
  };

  // ── Toggle between n_wrapper and regular condition ────────
  const handleTypeToggle = () => {
    if (type === 'n_wrapper') {
      // Unwrap — convert back to regular comparison
      onChange(index, {
        ...condition,
        type:     'comparison',
        operator: 'lt',
        left:     defaultSide('CLOSE'),
        right:    { variable: 'EMA', params: { period: 20, on: 'close', timeframe: 'auto', offset: 0 } },
        mode:     undefined,
        n:        undefined,
        inner:    undefined,
      });
    } else {
      // Wrap current condition into n_wrapper
      onChange(index, {
        ...condition,
        type:  'n_wrapper',
        mode:  'any',
        n:     5,
        inner: defaultInner(),
      });
    }
  };

  const opEntry = registry.operators[operator];
  const rhsType = opEntry?.rhs_type || 'variable';

  return (
    <div style={s.row}>
      {/* ── Connector (AND / OR) — hidden on first row ── */}
      <div style={s.connectorCell}>
        {!isFirst && (
          <div style={s.connectorGroup}>
            <button
              style={{ ...s.connBtn, ...(connector === 'AND' ? s.connBtnActive : {}) }}
              onClick={() => onConnector(index, 'AND')}
            >AND</button>
            <button
              style={{ ...s.connBtn, ...(connector === 'OR' ? s.connBtnActiveOr : {}) }}
              onClick={() => onConnector(index, 'OR')}
            >OR</button>
          </div>
        )}
      </div>

      {/* ── Condition body ── */}
      <div style={s.conditionBody}>

        {/* Any/All wrapper toggle */}
        <button
          style={{ ...s.wrapBtn, ...(type === 'n_wrapper' ? s.wrapBtnActive : {}) }}
          onClick={handleTypeToggle}
          title={type === 'n_wrapper' ? 'Click to unwrap' : 'Wrap with Any/All'}
        >
          {type === 'n_wrapper' ? '◎' : '○'}
        </button>

        {/* ── N WRAPPER ── */}
        {type === 'n_wrapper' ? (
          <NWrapperEditor
            condition={condition}
            onChange={updated => onChange(index, updated)}
            registry={registry}
          />
        ) : (
          /* ── REGULAR CONDITION ── */
          <div style={s.regularRow}>
            {/* LHS */}
            <SidePicker
              side={left || defaultSide('CLOSE')}
              onChange={val => {
                // Selecting V Pattern forces Occurred (no RHS);
                // leaving V Pattern restores a normal comparison.
                if (val.variable === 'V_PATTERN') {
                  onChange(index, {
                    ...condition,
                    type:     'v_pattern',
                    left:     val,
                    operator: 'occurred',
                    right:    null,
                  });
                } else if (left?.variable === 'V_PATTERN') {
                  onChange(index, {
                    ...condition,
                    type:     'comparison',
                    left:     val,
                    operator: 'gt',
                    right:    { variable: 'EMA', params: { period: 20, on: 'close', timeframe: 'auto', offset: 0 } },
                  });
                } else {
                  onChange(index, { ...condition, left: val });
                }
              }}
              registry={registry}
              isRhs={false}
            />

            {/* Operator */}
            <OperatorPicker
              operator={operator}
              onChange={handleOperatorChange}
              registry={registry}
            />

            {/* Extra input for up_by / down_by */}
            {opEntry?.extra_input && (
              <ExtraInput
                operatorKey={operator}
                extra={extra || {}}
                onChange={val => onChange(index, { ...condition, extra: val })}
                registry={registry}
              />
            )}

            {/* RHS — hidden for no-RHS operators (V Pattern Occurred) */}
            {rhsType !== 'none' && (
              <SidePicker
                side={right || defaultSide('EMA')}
                onChange={val => onChange(index, { ...condition, right: val })}
                registry={registry}
                isRhs={true}
                rhsType={rhsType}
              />
            )}
          </div>
        )}
      </div>

      {/* ── Delete button ── */}
      <button
        style={s.deleteBtn}
        onClick={() => onDelete(index)}
        title="Remove condition"
      >✕</button>
    </div>
  );
}


// ============================================================
// STYLES
// ============================================================
const s = {
  row: {
    display:      'flex',
    alignItems:   'flex-start',
    gap:          '6px',
    marginBottom: '6px',
  },
  connectorCell: {
    width:          '60px',
    flexShrink:     0,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'flex-end',
    paddingTop:     '4px',
  },
  connectorGroup: {
    display: 'flex',
    gap:     '2px',
  },
  connBtn: {
    padding:         '2px 6px',
    fontSize:        '10px',
    backgroundColor: '#f0f0f0',
    color:           '#666',
    border:          '1px solid #ddd',
    borderRadius:    '3px',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  connBtnActive: {
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          '1px solid #2962ff',
  },
  connBtnActiveOr: {
    backgroundColor: '#e65100',
    color:           '#fff',
    border:          '1px solid #e65100',
  },
  conditionBody: {
    flex:       1,
    display:    'flex',
    alignItems: 'flex-start',
    gap:        '4px',
    flexWrap:   'wrap',
  },
  regularRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        '4px',
    flexWrap:   'wrap',
    flex:       1,
  },
  sidePicker: {
    display:    'flex',
    alignItems: 'center',
    gap:        '3px',
    flexWrap:   'wrap',
  },
  select: {
    padding:         '3px 5px',
    fontSize:        '12px',
    border:          '1px solid #ddd',
    borderRadius:    '3px',
    backgroundColor: '#fff',
    color:           '#333',
    cursor:          'pointer',
    height:          '26px',
  },
  numInput: {
    padding:      '3px 5px',
    fontSize:     '12px',
    border:       '1px solid #ddd',
    borderRadius: '3px',
    color:        '#333',
    height:       '26px',
    textAlign:    'center',
  },
  wrapBtn: {
    width:           '22px',
    height:          '26px',
    fontSize:        '14px',
    backgroundColor: '#f0f0f0',
    border:          '1px solid #ddd',
    borderRadius:    '3px',
    cursor:          'pointer',
    color:           '#888',
    flexShrink:      0,
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    padding:         0,
  },
  wrapBtnActive: {
    backgroundColor: '#e8f0fe',
    color:           '#2962ff',
    border:          '1px solid #2962ff',
  },
  deleteBtn: {
    width:           '24px',
    height:          '26px',
    fontSize:        '12px',
    backgroundColor: '#fff0f0',
    color:           '#d32f2f',
    border:          '1px solid #ffcdd2',
    borderRadius:    '3px',
    cursor:          'pointer',
    flexShrink:      0,
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    padding:         0,
  },
  // N Wrapper styles
  nWrapperBox: {
    flex:         1,
    border:       '1px solid #c5d0e8',
    borderRadius: '4px',
    padding:      '6px 8px',
    background:   '#f5f8ff',
  },
  nWrapperHeader: {
    display:      'flex',
    alignItems:   'center',
    gap:          '6px',
    marginBottom: '6px',
  },
  toggleGroup: {
    display: 'flex',
    gap:     '0px',
  },
  toggleBtn: {
    padding:         '2px 8px',
    fontSize:        '11px',
    backgroundColor: '#f0f0f0',
    color:           '#666',
    border:          '1px solid #ddd',
    borderRadius:    '3px',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  toggleBtnActive: {
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          '1px solid #2962ff',
  },
  nLabel: {
    fontSize: '12px',
    color:    '#555',
  },
  innerRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        '4px',
    flexWrap:   'wrap',
  },
  forcedVar: {
    fontSize:     '12px',
    color:        '#2962ff',
    fontWeight:   'bold',
    padding:      '3px 6px',
    background:   '#e8f0fe',
    borderRadius: '3px',
    border:       '1px solid #c5d0e8',
    height:       '26px',
    display:      'flex',
    alignItems:   'center',
  },
};